import React, { useEffect, useState, useMemo, useCallback } from 'react'
import axios from 'axios'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { useAuthStatus, useToken, formatAmount, formatDateToYYYYMMDD } from '../../utils'
import { useContractDetail, useCancelContract } from '../../hooks'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { TourSectionForContract } from '../../components/TourSectionForContract'
import styles from './ContractManagementDetailView.module.scss'
import SpoeumModal from '../../components/modal/SpoeumModal'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../utils/incentiveDisplay'

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT

// 계약 타입별 필드 설정 정의
const CONTRACT_FIELD_CONFIGS = {
	termContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'salaryAmountForPeriod',
			label: '기간 급여액',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursement',
			label: '해외대회 지원금',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)}%`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용 여부',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
	domesticContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'weeklySalaryCutThrough',
			label: '컷통과',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'weeklySalaryCutFail',
			label: '컷탈락',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursementJeju',
			label: '제주 대회 지원금',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)} 원`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용 여부',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
	overseasContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'weeklySalaryCutThrough',
			label: '컷통과',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'weeklySalaryCutFail',
			label: '컷탈락',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursement',
			label: '해외 대회 지원금',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)} %`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용 여부',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
}

const truncateText = (text, maxLength) => {
	if (!text) return ''
	if (text.length <= maxLength) return text
	return text.substring(0, maxLength) + '...'
}

const formatPhoneNumber = (phoneNumber) => {
	if (!phoneNumber) return '' // 전화번호가 없으면 빈 문자열 반환

	// 숫자만 남기고 나머지 문자 제거
	const cleaned = ('' + phoneNumber).replace(/\D/g, '')

	const len = cleaned.length
	if (len === 10) {
		// 010-xxxx-xxxx (구식 01x 포함)
		return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
	} else if (len === 11) {
		// 010-xxxx-xxxx (최신)
		return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
	} else if (len === 8) {
		// 지역번호 없는 경우 (예: 1588-xxxx)
		return cleaned.replace(/(\d{4})(\d{4})/, '$1-$2')
	}
	// 그 외의 경우 (예: 지역번호 포함, 02-xxxx-xxxx 등)
	else if (cleaned.startsWith('02')) {
		if (len === 9) {
			// 02-xxx-xxxx
			return cleaned.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3')
		} else if (len === 10) {
			// 02-xxxx-xxxx
			return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3')
		}
	}
	return cleaned
}

const ContractManagementDetailView = ({ ...props }) => {
	const { contractId: urlContractId } = useParams()
	const [auth] = useAuthStatus()
	const token = useToken()
	const navigate = useNavigate()

	// React Query 훅으로 계약 상세 조회
	const {
		data: contract,
		isLoading: loading,
		isError,
		error,
		refetch,
	} = useContractDetail(urlContractId)

	// 계약 취소/거절 mutation 훅
	const cancelContractMutation = useCancelContract()

	const [areaType, setAreaType] = useState('domestic')
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isTourSelectionModalOpen, setIsTourSelectionModalOpen] = useState(false)
	const [tourSelectConfirmModalOpen, setTourSelectConfirmModalOpen] = useState(false)
	const [savedTours, setSavedTours] = useState([])
	const [d_day, setD_day] = useState(0)
	const [acceptDeadline, setAcceptDeadline] = useState(null)
	const [isContractAcceptModalOpen, setIsContractAcceptModalOpen] = useState(false)
	const [isCancelContractModalOpen, setIsCancelContractModalOpen] = useState(false)
	const [cancelContractUserType, setCancelContractUserType] = useState(null)
	const [remainingTime, setRemainingTime] = useState({ hours: 0, minutes: 0, seconds: 0 })
	const [isExpired, setIsExpired] = useState(false)

	// 상대방 정보 결정: 내가 프로면 캐디, 내가 캐디면 프로
	const counterpart = useMemo(() => {
		if (!contract) return null
		if (auth.user?.userType === 'tourPro') {
			return contract.caddie
		} else if (auth.user?.userType === 'tourCaddie') {
			return contract.player
		}
		return contract.caddie || contract.player
	}, [contract, auth.user?.userType])

	const handleAreaType = (areaType) => {
		setAreaType(areaType)
	}

	// 카운트다운 계산 (KST 기준) - 내가 서명할 차례일 때만 계산
	useEffect(() => {
		if (!contract || !auth.user) return

		const status = contract.contract_status
		const userType = auth.user?.userType

		// 내가 서명할 차례일 때만 카운트다운 계산
		const isMyTurn =
			(userType === 'tourPro' && status === 'PRE') ||
			(userType === 'tourCaddie' && status === 'PRO_ACCEPTED')

		if (!isMyTurn) {
			return
		}

		const calculateRemainingTime = () => {
			let sentAt = null
			let deadlineDays = 0

			// 상태에 따라 기준 시각과 마감 일수 결정
			if (status === 'PRE') {
				sentAt = contract.document_sent_to_pro_at
				deadlineDays = 1 // 프로는 1일
			} else if (status === 'PRO_ACCEPTED') {
				sentAt = contract.document_sent_to_caddie_at
				deadlineDays = 3 // 캐디는 3일
			}

			if (!sentAt) return

			// sentAt는 UTC 문자열 (예: "2025-11-03T09:17:27.885Z")
			// Date 객체는 내부적으로 UTC 밀리초를 저장하므로, getTime()으로 UTC 밀리초를 얻습니다
			const sentAtUTC = new Date(sentAt).getTime()

			// 마감 시각 (UTC) = 전송 시각 (UTC) + 마감 일수
			const deadlineUTC = sentAtUTC + deadlineDays * 24 * 60 * 60 * 1000

			// 현재 시각 (UTC 밀리초)
			const nowUTC = new Date().getTime()

			// 남은 시간 계산 (밀리초) - 둘 다 UTC이므로 차이를 직접 계산하면 정확합니다
			const remainingMs = deadlineUTC - nowUTC

			if (remainingMs <= 0) {
				setIsExpired(true)
				setRemainingTime({ hours: 0, minutes: 0, seconds: 0 })
				return
			}

			setIsExpired(false)

			// 총 남은 시간 계산 (시간 단위)
			const totalHours = Math.floor(remainingMs / (1000 * 60 * 60))
			const totalMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60))
			const totalSeconds = Math.floor((remainingMs % (1000 * 60)) / 1000)

			setRemainingTime({ hours: totalHours, minutes: totalMinutes, seconds: totalSeconds })
		}

		calculateRemainingTime()

		// 1초마다 업데이트
		const interval = setInterval(calculateRemainingTime, 1000)

		return () => clearInterval(interval)
	}, [contract, auth.user])

	// 프로의 투어 타입 가져오기 (contract.player에 이미 있음)
	const playerTourType = useMemo(() => {
		if (!contract?.player) return null
		return (
			contract.player.user_tour_pro?.tour_type || contract.player.tourPro?.tour_type || null
		)
	}, [contract?.player])

	// 경력 레벨 매핑
	const mapCareerLevelToText = {
		NEWBIE: '신입',
		ONE_TO_THREE: '1~3년차',
		FOUR_TO_SIX: '4~6년차',
		SEVEN_TO_TEN: '7~10년차',
		OVER_TEN: '10년 이상',
	}

	// 프로필 메인 정보 (타입 / 이름 / 커리어)
	const displayTitle = useMemo(() => {
		if (!counterpart) return ''

		const type = counterpart?.user_type === 'tourPro' ? '프로' : '캐디'
		const name = counterpart?.name
		const tour =
			counterpart?.user_type === 'tourPro'
				? counterpart.user_tour_pro?.tour_type
				: counterpart.user_tour_caddie?.tour_type
		const career =
			counterpart?.user_type === 'tourPro'
				? mapCareerLevelToText[counterpart.user_tour_pro?.career_level]
				: mapCareerLevelToText[counterpart.user_tour_caddie?.career_level]

		if (counterpart?.user_type === 'tourPro') {
			return `${tour} / ${name} / ${career}`
		} else {
			return `${type} / ${name} / ${career}`
		}
	}, [counterpart])

	// 프로필 서브 정보 (팀/소속 정보)
	const displaySubInfo = useMemo(() => {
		if (!counterpart) return ''

		if (counterpart?.user_type === 'tourPro') {
			// 프로: agency와 sponsor 표시
			let secondText = ''
			// agency는 객체 또는 문자열일 수 있음
			const agencyName =
				counterpart?.user_tour_pro?.agency?.name || counterpart?.user_tour_pro?.agency_name
			if (agencyName) {
				secondText = agencyName + ' / '
			} else {
				secondText = '무소속' + ' / '
			}
			// main_sponsor도 객체 또는 문자열일 수 있음
			const sponsorName =
				counterpart?.user_tour_pro?.main_sponsor?.name ||
				counterpart?.user_tour_pro?.main_sponsor
			if (sponsorName) {
				secondText += sponsorName
			} else {
				secondText += ' -'
			}
			return secondText
		} else if (counterpart?.user_type === 'tourCaddie') {
			// 캐디: 팀만 표시
			const teamName = counterpart?.user_tour_caddie?.caddie_team_name
			return teamName || '무소속'
		}

		return ''
	}, [counterpart])

	// 계약 상태에 따른 프로그레스 단계 계산
	const contractProgressSteps = useMemo(() => {
		if (!contract) return null

		const status = contract.contract_status

		// TERMINATED 등 종료 상태는 프로그레스 표시 안 함
		// CANCELED, EXPIRED는 프로그레스를 disabled 스타일로 표시
		if (status === 'TERMINATED') {
			return null
		}

		const steps = [
			{
				label: status === 'PRE' ? '프로 서명 대기' : '프로 서명 완료',
				status: 'PRE',
				completed: true, // 첫 번째 단계는 항상 완료됨 (계약 제안이 이미 이루어짐)
				current: status === 'PRE',
			},
			{
				label:
					status === 'PRO_ACCEPTED'
						? '캐디 서명 대기'
						: status === 'ACCEPTED'
							? '캐디 서명 완료'
							: '캐디 서명 대기',
				status: 'PRO_ACCEPTED',
				completed: status === 'PRO_ACCEPTED' || status === 'ACCEPTED',
				current: status === 'PRO_ACCEPTED',
			},
			{
				label: '계약 체결',
				status: 'ACCEPTED',
				completed: status === 'ACCEPTED',
				current: status === 'ACCEPTED',
			},
		]

		return steps
	}, [contract?.contract_status])

	// 계약 취소 여부 확인
	const isCancelled = useMemo(() => {
		if (!contract) return false
		return contract.contract_status === 'CANCELED' || contract.contract_status === 'EXPIRED'
	}, [contract?.contract_status])

	// displayIcon 렌더링을 useMemo로 최적화 - 사용자 타입별로 다르게 표시
	const displayIconContent = useMemo(() => {
		if (!counterpart) return null

		if (counterpart?.user_type === 'tourPro') {
			// 프로: 우승, TOP10, 컷 통과율 표시 (서버 데이터에 따라 null 체크)
			const proData = counterpart.user_tour_pro
			return (
				<>
					{proData?.wins && proData.wins > 0 && (
						<div className={styles.displayIcon}>우승 {proData.wins}회</div>
					)}
					{proData?.top10_count && proData.top10_count > 0 && (
						<div className={styles.displayIcon}>TOP10 {proData.top10_count}회</div>
					)}
					{proData?.cut_through_rate && (
						<div className={styles.displayIcon}>
							컷 통과율 {proData.cut_through_rate}%
						</div>
					)}
				</>
			)
		} else if (counterpart?.user_type === 'tourCaddie') {
			// 캐디: 언어능력, 주요 커리어 표시 (서버 데이터 구조에 맞게 수정)
			const caddieData = counterpart.user_tour_caddie
			return (
				<>
					{caddieData?.caddie_languages && caddieData.caddie_languages.length > 0 && (
						<div className={styles.displayIcon}>
							{caddieData.caddie_languages
								.map((lang) => lang.language_name)
								.join(', ')}{' '}
							가능
						</div>
					)}
					{caddieData?.caddie_careers && caddieData.caddie_careers.length > 0 && (
						<div className={styles.displayIcon}>
							{(() => {
								// 대표 대회 찾기
								const representativeCareer = caddieData.caddie_careers.find(
									(career) => career.is_representative,
								)
								if (representativeCareer) {
									return representativeCareer.tour?.name || '대표 대회 없음'
								}
								// 대표 대회가 없으면 첫 번째 대회
								return caddieData.caddie_careers[0]?.tour?.name || '대회 없음'
							})()}
						</div>
					)}
				</>
			)
		}
		return null
	}, [counterpart])

	// 인센티브 설정 정렬 함수
	const sortIncentiveSettings = useCallback((incentiveSettings) => {
		if (!incentiveSettings || !Array.isArray(incentiveSettings)) {
			return []
		}

		const sorted = incentiveSettings.sort((a, b) => {
			// startRank가 null이나 undefined인 경우 처리
			const aRank = a.startRank || 999
			const bRank = b.startRank || 999
			return aRank - bRank
		})

		return sorted
	}, [])

	// 필드 렌더링 헬퍼 함수들
	const renderContractField = useCallback((config, contractData) => {
		const value = contractData?.[config.key]

		return (
			<div key={config.key} className={styles.fieldContainer}>
				<div className={styles.fieldLabel}>{config.label}</div>
				<div className={styles.fieldContent}>
					{config.formatter ? config.formatter(value) : value}
				</div>
			</div>
		)
	}, [])

	const renderIncentiveField = useCallback(
		(config, incentives) => {
			return (
				<div key={config.key} className={styles.fieldContainer}>
					<div className={styles.fieldLabel}>{config.label}</div>
					<div className={styles.fieldContent}>
						{incentives && incentives.length > 0 ? (
							sortIncentiveSettings(incentives).map((setting, mapIndex) => {
								if (setting.percentage === null && setting.endRank === null)
									return null

								const displayRank =
									mapIndex === 0 || setting.startRank === 1
										? '우승 : '
										: setting.startRank +
											(setting.endRank !== null
												? setting.endRank === 0
													? ` ~ CUT :  `
													: ` ~ ${setting.endRank} :  `
												: '~ ')
								const displayPercentage =
									setting.percentage !== null
										? `  ${setting.percentage}%`
										: '미입력'
								return (
									<div key={mapIndex} className={styles.incentiveDisplayItem}>
										<div>{displayRank}</div>
										<div>{displayPercentage}</div>
									</div>
								)
							})
						) : (
							<div className={styles.incentiveDisplayItem}>인센티브 정보 없음</div>
						)}
					</div>
				</div>
			)
		},
		[sortIncentiveSettings],
	)

	const renderContractFields = useCallback(
		(contractType, contractData) => {
			const fields = CONTRACT_FIELD_CONFIGS[contractType] || []

			return fields.map((field) => {
				if (field.type === 'incentive') {
					return renderIncentiveField(field, contractData?.[field.key])
				}
				return renderContractField(field, contractData)
			})
		},
		[renderContractField, renderIncentiveField],
	)

	const handleContractAccept = async () => {
		// try {
		// 	const response = await axios.patch(
		// 		`${API_BASE_URL}/contract/accept/${contract.id}`,
		// 		{},
		// 		{
		// 			headers: {
		// 				Authorization: `Bearer ${token}`,
		// 			},
		// 		},
		// 	)
		// 	console.log('response : ', response)
		// 	setIsContractAcceptModalOpen(false)
		// 	refetch()
		// } catch (error) {
		// 	console.error('계약 수락 실패:', error)
		// }
	}

	// 계약 데이터가 로드되면 초기 설정
	useEffect(() => {
		if (!contract) return

		if (contract.domesticContract === null && contract.overseasContract !== null) {
			setAreaType('oversea')
		}
	}, [contract])

	// 계약 수락 후 refetch
	// useEffect(() => {
	// 	if (!isContractAcceptModalOpen) {
	// 		refetch()
	// 	}
	// }, [isContractAcceptModalOpen, refetch])

	useEffect(() => {
		console.log('Contract state updated:', contract)
	}, [contract])

	if (loading) {
		return <div>로딩 중...</div>
	}

	if (isError || error) {
		return (
			<div style={{ color: 'red' }}>
				{error?.message || '계약 정보를 불러오는 데 실패했습니다.'}
			</div>
		)
	}

	if (!contract) {
		return <div>계약 정보가 없습니다.</div>
	}

	const tourDates = contract.contractTours

	const sortedTourItems = [...contract.contractTours].sort((a, b) => {
		// 2. started_at을 Date 객체로 변환하여 비교합니다.
		const dateA = new Date(a.tour.started_at)
		const dateB = new Date(b.tour.started_at)

		// 3. 오름차순으로 정렬합니다 (이른 날짜가 먼저 오도록).
		return dateA.getTime() - dateB.getTime()
	})

	let startDate = null
	let endDate = null
	if (sortedTourItems.length > 0) {
		const startDates = sortedTourItems.map((tourItem) => new Date(tourItem.tour.started_at))
		const endDates = sortedTourItems.map((tourItem) => new Date(tourItem.tour.ended_at))
		startDate = new Date(Math.min(...startDates))
		endDate = new Date(Math.max(...endDates))
	}

	// 모달 열기 함수
	const openModal = () => {
		setIsModalOpen(true)
	}

	const openTourSelectionModal = () => {
		setIsTourSelectionModalOpen(true)
	}

	// 모달 닫기 함수
	const closeModal = () => {
		setIsModalOpen(false)
		setIsTourSelectionModalOpen(false)
	}

	// 계약 취소/거절 모달 열기
	const handleCancelContractClick = (userType) => {
		setCancelContractUserType(userType)
		setIsCancelContractModalOpen(true)
	}

	// 계약 취소/거절 핸들러
	const handleCancelContract = async () => {
		if (!cancelContractUserType) return

		const byParam = cancelContractUserType === 'tourPro' ? 'pro' : 'caddie'

		try {
			await cancelContractMutation.mutateAsync({
				contractId: contract.id,
				by: byParam,
			})
			alert(
				cancelContractUserType === 'tourPro'
					? '계약 요청이 취소되었습니다.'
					: '계약 요청이 거절되었습니다.',
			)
			setIsCancelContractModalOpen(false)
			setCancelContractUserType(null)
			// navigate('/contracts') // 계약 목록 페이지로 이동
		} catch (error) {
			console.error('계약 취소/거절 실패:', error)
			alert('계약 취소/거절에 실패했습니다.')
		}
	}

	// '계약 해지하기' 버튼 클릭 시 실행될 함수
	const handleConfirmTermination = async () => {
		console.log('계약 해지 요청 시작...')
		try {
			const response = await axios.patch(
				`${API_BASE_URL}/contract/${contract.id}/${'REJECTED'}`,
			)
			alert('계약 해지 요청이 성공적으로 처리되었습니다!')
			closeModal() // 모달 닫기
			navigate('/contracts') // 계약 목록 페이지로 이동 등
		} catch {
			alert('계약 해지 요청이 실패하였습니다.')
		}
	}

	const handleChangeTourIds = async (tourIds) => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}/contract/${contract.id}/add-tours`,
				{ tourIds: tourIds },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			)
			console.log('response for add-tours: ', response)

			window.location.reload()
			alert('대회 지정에 성공했습니다.')
		} catch {
			alert('대회 지정에 실패했습니다.')
		}
	}

	const openCotractPaper = async () => {
		if (!contract?.contract_document_id || !token) {
			console.error('문서 ID 또는 토큰이 없습니다.')
			alert('계약서를 불러올 수 없습니다.')
			return
		}

		try {
			// API에서 직접 import
			const { downloadContractDocument } = await import('../../services/api')
			const pdfBlob = await downloadContractDocument(contract.contract_document_id, token)

			// Blob이 비어있는지 확인
			if (!pdfBlob || pdfBlob.size === 0) {
				throw new Error('다운로드된 파일이 비어있습니다.')
			}

			// Blob의 타입 확인
			if (pdfBlob.type !== 'application/pdf') {
				console.warn('응답 타입이 PDF가 아닙니다:', pdfBlob.type)
			}

			// Blob을 URL로 변환하여 새 탭에서 열기
			const pdfURL = window.URL.createObjectURL(pdfBlob)

			// 임시 anchor 태그를 생성하여 새 탭에서 열기
			const link = document.createElement('a')
			link.href = pdfURL
			link.target = '_blank'
			link.rel = 'noopener noreferrer'
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			// 일정 시간 후 URL 해제 (브라우저가 PDF를 로드할 시간 확보)
			setTimeout(() => {
				window.URL.revokeObjectURL(pdfURL)
			}, 10000) // 새 탭에서 로드될 시간을 더 여유있게
		} catch (error) {
			console.error('계약서 다운로드 실패:', error)
			const errorMessage =
				error.response?.status === 204
					? '계약서가 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요.'
					: error.message || '계약서 다운로드에 실패했습니다.'
			alert(errorMessage)
		}
	}

	const TerminationModal = ({ show, onClose, onConfirm, caddieName, tourList }) => {
		const [agreed, setAgreed] = useState(false)

		if (!show) {
			return null // show prop이 false면 아무것도 렌더링하지 않음
		}

		// 오늘 날짜를 기준으로 과거인 대회를 필터링하는 로직
		const today = new Date()
		today.setHours(0, 0, 0, 0) // 시간을 0으로 설정하여 날짜만 비교

		const filteredTourList = tourList
			? tourList.filter((tourItem) => {
					if (!tourItem.tour || !tourItem.tour.started_at) {
						return false // tour 또는 started_at이 없으면 필터링
					}
					const tourStartDate = new Date(tourItem.tour.started_at)
					tourStartDate.setHours(0, 0, 0, 0) // 시간을 0으로 설정하여 날짜만 비교
					return tourStartDate >= today // 오늘 날짜보다 같거나 늦은 대회만 포함
				})
			: [] // tourList가 null/undefined일 경우 빈 배열 반환

		return (
			<div className="modal-overlay" onClick={onClose}>
				<div className="modal-content" onClick={(e) => e.stopPropagation()}>
					{contract.termContract !== null ? (
						<h3 className="modal-header">기간계약 해지 신청</h3>
					) : (
						<h3 className="modal-header">중도해지</h3>
					)}

					<p className="main-question">
						'<span className="caddie-name">{caddieName}</span>' 님과의
						<br />
						계약을 해지하시겠어요?
					</p>

					{contract.termContract !== null ? (
						<p className="info-text">
							계약 중도 해지 신청은 되돌릴 수 없으며, 향후 플랫폼 활동에 제한이 걸릴
							수 있고, 위약금이 발생할 수 있습니다.
						</p>
					) : (
						<p className="info-text">
							계약 중도 해지 신청은 되돌릴 수 없으며, 남은 대회에 대한 계약은 즉시
							종료됩니다. 향후 플랫폼 활동에 제한이 걸릴 수 있고, 위약금이 발생할 수
							있습니다.
						</p>
					)}
					<div className="policy-link">중도 해지 정책 안내</div>

					{contract.termContract !== null ? (
						''
					) : (
						<div className="cancel_tour_title">해지 가능 대회목록</div>
					)}

					{contract.termContract !== null ? (
						<div>
							<div className="term_cancel_process_title">기간계약 해지 프로세스</div>
							<div className="term_cancel_process_announcement">
								<div>
									<div>해지신청</div>
								</div>
								<div>
									<div>스포이음 심사</div>
								</div>
								<div>
									<div>신청 결과 안내</div>
								</div>
							</div>
							<div className="progress_bar">
								<div>
									<svg
										width="9"
										height="8"
										viewBox="0 0 9 8"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<rect
											x="0.666626"
											width="8"
											height="8"
											rx="4"
											fill="#E4E4E4"
										/>
									</svg>
								</div>
								<div>
									<svg
										width="9"
										height="8"
										viewBox="0 0 9 8"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<rect
											x="0.666626"
											width="8"
											height="8"
											rx="4"
											fill="#C9C9C9"
										/>
									</svg>
								</div>
								<div>
									<svg
										width="9"
										height="8"
										viewBox="0 0 9 8"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<rect
											x="0.666626"
											width="8"
											height="8"
											rx="4"
											fill="#A0A0A0"
										/>
									</svg>
								</div>
								<div>
									<svg
										width="6"
										height="12"
										viewBox="0 0 6 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M5.85355 5.64645C6.04882 5.84171 6.04882 6.15829 5.85355 6.35355L0.853553 11.3536C0.658292 11.5488 0.341709 11.5488 0.146447 11.3536C-0.0488156 11.1583 -0.0488156 10.8417 0.146447 10.6464L4.79289 6L0.146447 1.35355C-0.0488152 1.15829 -0.0488152 0.841709 0.146447 0.646447C0.341709 0.451184 0.658292 0.451184 0.853554 0.646447L5.85355 5.64645Z"
											fill="#DDDDDD"
										/>
									</svg>
								</div>
								<div>
									<svg
										width="6"
										height="12"
										viewBox="0 0 6 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M5.85355 5.64645C6.04882 5.84171 6.04882 6.15829 5.85355 6.35355L0.853553 11.3536C0.658292 11.5488 0.341709 11.5488 0.146447 11.3536C-0.0488156 11.1583 -0.0488156 10.8417 0.146447 10.6464L4.79289 6L0.146447 1.35355C-0.0488152 1.15829 -0.0488152 0.841709 0.146447 0.646447C0.341709 0.451184 0.658292 0.451184 0.853554 0.646447L5.85355 5.64645Z"
											fill="#A0A0A0"
										/>
									</svg>
								</div>
							</div>
						</div>
					) : (
						filteredTourList &&
						filteredTourList.length > 0 && (
							<div className="tour_list_container">
								{filteredTourList.map(
									(
										tourItem,
										index, // 필터링된 리스트 맵핑
									) => (
										<div key={tourItem.tour?.id || `tour-${index}`}>
											{tourItem.tour?.name || '대회명 없음'}
											<br />
											<span className="tour-dates">
												{formatDateToYYYYMMDD(tourItem.tour?.started_at)} ~{' '}
												{formatDateToYYYYMMDD(tourItem.tour?.ended_at)}
											</span>
										</div>
									),
								)}
							</div>
						)
					)}

					<div className="agree-check">
						{/* agreed 상태에 따라 custom-checkbox-label에 'is-checked' 클래스를 동적으로 추가 */}
						<label
							htmlFor="agreeCheckbox"
							className={`custom-checkbox-label ${agreed ? 'is-checked' : ''}`}>
							위 내용을 확인하고 동의합니다. {/* 텍스트를 먼저 배치 */}
							<input
								type="checkbox"
								id="agreeCheckbox"
								checked={agreed}
								onChange={(e) => setAgreed(e.target.checked)}
								className="hidden-checkbox" // 이 클래스로 input을 숨깁니다.
							/>
							{/* SVG 아이콘들을 input 바로 뒤에 배치하여 CSS의 (+) 셀렉터 활용 */}
							{/* 체크되지 않았을 때의 SVG 아이콘 */}
							<svg
								className="checkbox-icon-unchecked"
								width="18"
								height="18"
								viewBox="0 0 18 18"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<circle cx="9" cy="9" r="9" fill="#ECECEC" />
								<path
									d="M7.5 12.5L4 9L5.5 7.5L7.5 9.5L12.5 4.5L14 6L7.5 12.5Z"
									fill="white"
								/>
							</svg>
							{/* 체크되었을 때의 SVG 아이콘 */}
							<svg
								className="checkbox-icon-checked"
								width="18"
								height="18"
								viewBox="0 0 18 18"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<circle cx="9" cy="9" r="9" fill="#1B1B1B" />
								<path
									d="M7.5 12.5L4 9L5.5 7.5L7.5 9.5L12.5 4.5L14 6L7.5 12.5Z"
									fill="white"
								/>
							</svg>
						</label>
					</div>

					<div className="button-container">
						<button
							className="modal-button primary"
							onClick={onConfirm}
							disabled={!agreed}>
							계약 해지하기
						</button>
						<button className="modal-button secondary" onClick={onClose}>
							닫기
						</button>
					</div>
				</div>
			</div>
		)
	}

	const TourSelectionModal = ({ show, onClose, isOverseasIncluded }) => {
		const [toursCheckedByChild, setToursCheckedByChild] = useState([])
		const [noToursForSelectedGender, setNoToursForSelectedGender] = useState(false)

		if (!show) {
			return null
		}

		const handleSelectedToursFromChild = (selectedIds) => {
			setToursCheckedByChild(selectedIds)
		}

		const handleNoToursAvailable = (isNoTours) => {
			setNoToursForSelectedGender(isNoTours)
		}

		return (
			<div className="modal-overlay" onClick={onClose}>
				<div className="modal-content" onClick={(e) => e.stopPropagation()}>
					<div className="modal_select_tour_title">
						진행할 대회를 지정해주세요(수정불가)
					</div>
					<TourSectionForContract
						API_ENDPOINT={API_BASE_URL}
						TourCount={false}
						selectOnlyOne={false}
						onSelectedToursChange={handleSelectedToursFromChild}
						onNoToursAvailable={handleNoToursAvailable}
						previousSelectedTours={savedTours}
						isOverseasIncluded={isOverseasIncluded}></TourSectionForContract>
					{tourSelectConfirmModalOpen && (
						<div className={styles.confirmModalContainer}>
							<span className={styles.confirmMain}>
								선택한 대회로 확정하시겠어요?
							</span>
							<span className={styles.confirmSub}>
								대회를 한번 지정한 이후에는 수정이 불가능해요.
							</span>
							<span className={styles.confirmSub}>이대로 대회를 확정하시겠어요?</span>
							<div className={styles.buttons}>
								<button
									className={styles.cancel}
									onClick={() => {
										setSavedTours([])
										setTourSelectConfirmModalOpen(false)
									}}>
									취소
								</button>
								<button
									className={styles.confirm}
									onClick={() => {
										handleChangeTourIds(savedTours)
										setTourSelectConfirmModalOpen(false)
									}}>
									확정하기
								</button>
							</div>
						</div>
					)}
					<div
						className={`tour_selection_complete_button ${toursCheckedByChild.length !== 0 ? 'tour_selection_complete_button_active' : 'tour_selection_complete_button_disactive'}`}
						// onClick={() => handleChangeTourIds(toursCheckedByChild)}>
						onClick={() => {
							setSavedTours(toursCheckedByChild)
							setTourSelectConfirmModalOpen(true)
						}}>
						완료
					</div>
					<div className="tour_selection_close_button" onClick={onClose}>
						닫기
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.contractManagementDetailContainer}>
			{isContractAcceptModalOpen && (
				<SpoeumModal
					onClose={() => setIsContractAcceptModalOpen(false)}
					onConfirm={handleContractAccept}
					title="수락하기"
					detail={
						<>
							계약을 수락하면 상대방의 연락처가 공유되고
							<br />
							즉시 계약이 성사돼요! 지금 수락하시겠어요?
						</>
					}
					confirmButtonText="계약 수락하기"
					cancelButtonText="취소"
				/>
			)}
			{isCancelContractModalOpen && cancelContractUserType && (
				<SpoeumModal
					onClose={() => {
						setIsCancelContractModalOpen(false)
						setCancelContractUserType(null)
					}}
					onConfirm={handleCancelContract}
					title={
						cancelContractUserType === 'tourPro' ? '계약 제안 취소' : '계약 요청 거절'
					}
					detail={
						cancelContractUserType === 'tourPro' ? (
							<>
								아직 계약이 체결되지 않아, 제안 취소가 가능해요. 계약 제안을
								취소하시겠어요? 취소된 계약 제안은 다시 되돌릴 수 없어요.
							</>
						) : (
							<>
								계약 요청을 거절하시겠어요?
								<br />
								거절한 계약 요청은 다시 취소할 수 없어요.
							</>
						)
					}
					confirmButtonText={
						cancelContractUserType === 'tourPro'
							? '계약 요청 취소하기'
							: '계약 거절하기'
					}
					cancelButtonText="취소"
				/>
			)}
			<div className={styles.backButton} onClick={() => navigate(-1)}>
				<div>
					<svg
						width="12"
						height="10"
						viewBox="0 0 12 10"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							fillRule="evenodd"
							clipRule="evenodd"
							d="M5.43436 0.315641C5.60521 0.486495 5.60521 0.763505 5.43436 0.934359L1.80622 4.5625H11.25C11.4916 4.5625 11.6875 4.75838 11.6875 5C11.6875 5.24162 11.4916 5.4375 11.25 5.4375H1.80622L5.43436 9.06564C5.60521 9.2365 5.60521 9.5135 5.43436 9.68436C5.2635 9.85521 4.9865 9.85521 4.81564 9.68436L0.440641 5.30936C0.269786 5.1385 0.269786 4.8615 0.440641 4.69064L4.81564 0.315641C4.9865 0.144786 5.2635 0.144786 5.43436 0.315641Z"
							fill="#A0A0A0"
						/>
					</svg>
				</div>
				<div>뒤로</div>
			</div>
			{/* 계약 진행 상태 프로그레스 */}
			{contractProgressSteps && (
				<div className={styles.contractProgressContainer}>
					<div
						className={`${styles.contractProgressBar} ${
							isCancelled ? styles.disabled : ''
						}`}>
						{contractProgressSteps.map((step, index) => (
							<React.Fragment key={step.status}>
								<div
									className={`${styles.contractProgressStep} ${
										step.completed && !isCancelled ? styles.completed : ''
									} ${step.current && !isCancelled ? styles.current : ''} ${
										isCancelled ? styles.disabled : ''
									}`}>
									<div className={styles.contractProgressStepDot}></div>
									<div className={styles.contractProgressStepLabel}>
										{step.label}
									</div>
								</div>
								{index < contractProgressSteps.length - 1 && (
									<div
										className={`${styles.contractProgressLine} ${
											contractProgressSteps[index + 1]?.completed &&
											!isCancelled
												? styles.completed
												: ''
										} ${isCancelled ? styles.disabled : ''}`}></div>
								)}
							</React.Fragment>
						))}
					</div>
				</div>
			)}
			<div className={styles.contractDetailCardContainer}>
				<div className={styles.contractDetailCardMainContainer}>
					{/* 프로세스 진행단계 */}

					{/* 상대방 프로필 정보 */}
					<div className={styles.mainContent}>
						<div className={styles.profileImageArea}>
							{counterpart?.profile_image || counterpart?.profile_type ? (
								<img
									src={counterpart.profile_image || counterpart.profile_type}
									alt={`${counterpart.name} 프로필`}
									className={styles.profileImage}
									onError={(e) => {
										e.target.src = '/images/Onboard-default-image.png'
									}}
								/>
							) : (
								<img
									src="/images/Onboard-default-image.png"
									alt="기본 프로필"
									className={styles.profileImage}
								/>
							)}
						</div>
						<div className={styles.profileDetailArea}>
							<div className={styles.profileMainInfo}>{displayTitle}</div>
							{displaySubInfo && (
								<div className={styles.profileSubInfo}>{displaySubInfo}</div>
							)}
						</div>
						<div className={styles.caddiePhoneNumber}>
							<div style={{ display: 'flex', alignItems: 'center', padding: ' 4px' }}>
								<svg
									width="12"
									height="12"
									viewBox="0 0 12 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg">
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M0.75 2.25C0.75 1.42157 1.42157 0.75 2.25 0.75H2.93581C3.366 0.75 3.74099 1.04278 3.84532 1.46012L4.39822 3.67171C4.4897 4.03765 4.35297 4.42277 4.05121 4.64909L3.4044 5.1342C3.33724 5.18457 3.32263 5.25834 3.3415 5.30984C3.90925 6.85974 5.14026 8.09075 6.69016 8.6585C6.74166 8.67737 6.81543 8.66276 6.8658 8.5956L7.35091 7.94879C7.57723 7.64703 7.96235 7.5103 8.32829 7.60178L10.5399 8.15468C10.9572 8.25901 11.25 8.634 11.25 9.06419V9.75C11.25 10.5784 10.5784 11.25 9.75 11.25H8.625C4.27576 11.25 0.75 7.72424 0.75 3.375V2.25Z"
										fill="#7D7D7D"
									/>
								</svg>
							</div>
							{contract.contract_status === 'ACCEPTED' ? (
								<div>{formatPhoneNumber(counterpart?.phone)}</div>
							) : (
								<div
									style={{
										whiteSpace: 'nowrap',
										display: 'flex',
										alignItems: 'center',
									}}>
									계약이 체결되면 연락처가 표시되요
								</div>
							)}
						</div>
					</div>
					{/* <div className={styles.displayIconContainer}>
						<div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
							{displayIconContent}
						</div>
					</div> */}
				</div>
			</div>

			{/* 계약서 보기: PDF 준비 상태 및 취소 상태에 따라 제어 */}
			<div
				className={`${styles.contractDocumentButton} ${
					contract?.is_pdf_ready && !isCancelled ? '' : styles.disabled
				}`}
				onClick={
					contract?.is_pdf_ready && !isCancelled ? () => openCotractPaper() : undefined
				}
				aria-disabled={!contract?.is_pdf_ready || isCancelled}
				role="button">
				계약서 보기
			</div>
			{!contract?.is_pdf_ready && !isCancelled && (
				<div className={styles.pdfPreparingContainer}>
					<div className={styles.pdfPreparingText}>
						<span>계약문서를 생성중이에요. 잠시만 기다려주세요.</span>
					</div>
					<button className={styles.pdfRefreshButton} onClick={() => refetch()}>
						<ArrowPathIcon className={styles.pdfRefreshIcon} />
						새로고침
					</button>
				</div>
			)}
			{isCancelled && (
				<div className={styles.pdfPreparingContainer}>
					<div className={styles.pdfPreparingText}>
						<span>계약이 취소되었습니다</span>
					</div>
				</div>
			)}

			{/* 서명 안내 블록 (내가 서명할 차례일 때만 표시) */}
			{((auth.user?.userType === 'tourPro' && contract?.contract_status === 'PRE') ||
				(auth.user?.userType === 'tourCaddie' &&
					contract?.contract_status === 'PRO_ACCEPTED')) && (
				<div className={styles.signNoticeContainer}>
					<div className={styles.signNoticeTitle}>
						<span style={{ color: '#085e4b' }}>카카오톡/이메일</span>을 확인하여 서명을
						완료해 주세요.
					</div>
					<div className={styles.signNoticeDesc}>
						기한 내 서명이 완료되면 계약이 성사됩니다.
					</div>
					<div className={`${styles.signCountdown} ${isExpired ? styles.expired : ''}`}>
						({String(remainingTime.hours).padStart(2, '0')}:
						{String(remainingTime.minutes).padStart(2, '0')}:
						{String(remainingTime.seconds).padStart(2, '0')})
					</div>
					<div className={styles.signNoticeWarning}>
						<strong>기한 내 미서명 시, 계약 제안이 취소돼요</strong>
					</div>
				</div>
			)}

			{/* 프로 사용자에게만 취소 버튼 표시 (PRE 상태일 때만) */}
			{auth?.user?.userType === 'tourPro' && contract?.contract_status === 'PRE' && (
				<div
					className={styles.cancelRequestButton}
					onClick={() => handleCancelContractClick('tourPro')}>
					계약 요청 취소하기
				</div>
			)}

			{/* 캐디 사용자에게만 거절 버튼 표시 (PRO_ACCEPTED 상태일 때만) */}
			{auth?.user?.userType === 'tourCaddie' &&
				contract?.contract_status === 'PRO_ACCEPTED' && (
					<div
						className={styles.cancelRequestButton}
						onClick={() => handleCancelContractClick('tourCaddie')}>
						계약 거절하기
					</div>
				)}

			{/* 대회 목록 */}
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
					marginTop: '16px',
				}}>
				{contract.contract_status === 'ACCEPTED' && (
					<div className={styles.detailedTourListContainer}>
						<div className={styles.detailedTourListContainerHeader}>
							<div>정산 현황</div>
							{sortedTourItems.length !== 0 ? (
								sortedTourItems.map((tourItem) => (
									<div
										key={tourItem.tour.id}
										className={`${styles.detailTourStatus} ${
											tourItem.paymentStatus === 'PAYMENT_COMPLETED'
												? styles.paymentDisabled
												: ''
										}`}>
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: '2px',
											}}>
											<div
												style={{
													fontSize: '14px',
													fontFamily: 'Pretendard-Medium',
												}}>
												{tourItem.tour.name}
											</div>
											<div
												style={{
													fontSize: '12px',
													fontFamily: 'Pretendard-Regular',
												}}>
												{formatDateToYYYYMMDD(tourItem.tour.started_at)} ~{' '}
												{formatDateToYYYYMMDD(tourItem.tour.ended_at)}
											</div>
										</div>
										{tourItem.paymentStatus === 'BEFORE_TOUR_PERIOD' && (
											<div className={styles.beforeTour}>-</div>
										)}
										{tourItem.paymentStatus === 'WAITING_FOR_PAYMENT' && (
											<div className={styles.waitingPayment}>입금대기</div>
										)}
										{tourItem.paymentStatus === 'UNDER_REVIEW' && (
											<div className={styles.underReview}>검토중</div>
										)}
										{tourItem.paymentStatus === 'PAYMENT_COMPLETED' && (
											<div className={styles.paymentComplete}>입금완료</div>
										)}
										{tourItem.paymentStatus === 'SETTLEMENT_PENDING' && (
											<div className={styles.settlementPending}>정산전</div>
										)}
									</div>
								))
							) : (
								<div className={styles.detailTourStatus}>
									{contract.contract_type === 'year_first_half' && (
										<React.Fragment>
											<div>
												<div>기간계약(상반기)</div>
											</div>
											<div>나중에 상태 추가</div>
										</React.Fragment>
									)}
									{contract.contract_type === 'year_second_half' && (
										<React.Fragment>
											<div>
												<div>기간계약(하반기)</div>
											</div>
											<div>나중에 상태 추가</div>
										</React.Fragment>
									)}
									{contract.contract_type === 'both' && (
										<React.Fragment>
											<div>
												<div>기간계약(상반기/하반기)</div>
											</div>
											<div>나중에 상태 추가</div>
										</React.Fragment>
									)}
								</div>
							)}
						</div>
					</div>
				)}
				{contract.termContract === null ? (
					<div className={styles.basicContractInformation}>
						<div>기본 계약 정보</div>
						{/* {contract.post_id !== null ? (
						<div className={styles.postInformation}>
							<div>지원한 공고명</div>
							<div>{contract.job_post.post_subject}</div>
						</div>
					) : (
						<div className={styles.postInformation}>
							<div>지원한 공고명</div>
							<div>공고 지원이 아닙니다.</div>
						</div>
					)} */}
						<div className={styles.tourTypeInformation}>
							<div>투어 종류</div>
							<div>{playerTourType || '-'}</div>
						</div>
						<div className={styles.contractTypeInformation}>
							<div>계약 종류</div>
							{contract.contract_type === 'single' && <div>단일계약</div>}
							{contract.contract_type === 'long_term' && <div>장기계약</div>}
							{contract.contract_type === 'year_first_half' && (
								<div>기간계약(상반기)</div>
							)}
							{contract.contract_type === 'year_second_half' && (
								<div>기간계약(하반기)</div>
							)}
							{contract.contract_type === 'both' && (
								<div>기간계약(상반기/하반기)</div>
							)}
						</div>
						<div className={styles.contractDurationInformation}>
							<div>계약기간</div>
							<div>
								{contract.termContract !== null
									? contract.contract_started_at &&
										contract.contract_ended_at && (
											<>
												{formatDateToYYYYMMDD(contract.contract_started_at)}{' '}
												~ {formatDateToYYYYMMDD(contract.contract_ended_at)}
											</>
										)
									: formatDateToYYYYMMDD(startDate) &&
										formatDateToYYYYMMDD(endDate) && (
											<>
												{formatDateToYYYYMMDD(startDate)} ~{' '}
												{formatDateToYYYYMMDD(endDate)}
											</>
										)}
							</div>
						</div>
						<div className={styles.tourListSelectionButton}>
							<div>대회목록</div>
							{contract.contractTours.length === 0 ? (
								<div
									className={styles.userSelectTour}
									onClick={openTourSelectionModal}>
									<span
										style={{
											fontFamily: 'Pretendard-Medium',
											color: '#085e4b',
										}}>
										대회 지정하기
									</span>
								</div>
							) : (
								<div className={styles.tourListOuterContainer}>
									{sortedTourItems.map((tourItem, index) => (
										<div key={tourItem.tour.id}>
											<div>({index + 1})</div>
											<div>
												<div>{tourItem.tour.name}</div>
												<div>
													{formatDateToYYYYMMDD(tourItem.tour.started_at)}{' '}
													~ {formatDateToYYYYMMDD(tourItem.tour.ended_at)}
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
						<div className={styles.isOverseasContractIncluded}>
							<div>해외 대회 포함 여부</div>
							{contract.overseasContract !== null ||
							contract.termContract?.isOverseasIncluded === true ? (
								<div>해외대회 포함</div>
							) : (
								<div>해외대회 미포함</div>
							)}
						</div>
					</div>
				) : (
					<div className={styles.basicContractInformation}>
						<div>기본 계약 정보</div>
						{/* {contract.post_id !== null ? (
							<div className={styles.postInformation}>
								<div>지원한 공고명</div>
								<div>{contract.job_post.post_subject}</div>
							</div>
						) : (
							<div className={styles.postInformation}>
								<div>지원한 공고명</div>
								<div>공고 지원이 아닙니다.</div>
							</div>
						)} */}
						<div className={styles.tourTypeInformation}>
							<div>투어 종류</div>
							<div>{playerTourType || '-'}</div>
						</div>
						<div className={styles.contractTypeInformation}>
							<div>계약 종류</div>
							{contract.contract_type === 'single' && <div>단일계약</div>}
							{contract.contract_type === 'long_term' && <div>장기계약</div>}
							{contract.contract_type === 'year_first_half' && (
								<div>기간계약(상반기)</div>
							)}
							{contract.contract_type === 'year_second_half' && (
								<div>기간계약(하반기)</div>
							)}
							{contract.contract_type === 'both' && (
								<div>기간계약(상반기/하반기)</div>
							)}
						</div>
						<div className={styles.contractDurationInformation}>
							<div>계약기간</div>
							<div>
								{contract.termContract !== null
									? contract.contract_started_at &&
										contract.contract_ended_at && (
											<>
												{formatDateToYYYYMMDD(contract.contract_started_at)}{' '}
												~ {formatDateToYYYYMMDD(contract.contract_ended_at)}
											</>
										)
									: formatDateToYYYYMMDD(startDate) &&
										formatDateToYYYYMMDD(endDate) && (
											<>
												{formatDateToYYYYMMDD(startDate)} ~{' '}
												{formatDateToYYYYMMDD(endDate)}
											</>
										)}
							</div>
						</div>
						<div className={styles.tourListSelectionButton}>
							<div>대회목록</div>
							{contract.contractTours.length === 0 ? (
								<div
									className={styles.userSelectTour}
									onClick={openTourSelectionModal}>
									<span
										style={{
											fontFamily: 'Pretendard-Medium',
											color: '#085e4b',
										}}>
										대회 지정하기
									</span>
								</div>
							) : (
								<div className={styles.tourListOuterContainer}>
									{sortedTourItems.map((tourItem, index) => (
										<div key={tourItem.tour.id}>
											<div>({index + 1})</div>
											<div>
												<div>{tourItem.tour.name}</div>
												<div>
													{formatDateToYYYYMMDD(tourItem.tour.started_at)}{' '}
													~ {formatDateToYYYYMMDD(tourItem.tour.ended_at)}
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
						<div className={styles.isOverseasContractIncluded}>
							<div>해외 대회 포함 여부</div>
							{contract.overseasContract !== null ||
							contract.termContract?.isOverseasIncluded === true ? (
								<div>해외대회 포함</div>
							) : (
								<div>해외대회 미포함</div>
							)}
						</div>
					</div>
				)}
				<div className={styles.detailContractInformation}>
					<div>상세 계약정보</div>
					{contract.termContract === null ? (
						<>
							{(contract.domesticContract !== null ||
								contract.overseasContract !== null) && (
								<div className={styles.areaSelectButton}>
									{contract.domesticContract !== null && (
										<div
											onClick={() => handleAreaType('domestic')}
											className={
												areaType === 'domestic' ? styles.active : ''
											}>
											국내
										</div>
									)}
									{contract.overseasContract !== null && (
										<div
											onClick={() => handleAreaType('oversea')}
											className={areaType === 'oversea' ? styles.active : ''}>
											해외
										</div>
									)}
								</div>
							)}
							<div className={styles.detailContractInformationItemContainer}>
								{areaType === 'domestic'
									? renderContractFields(
											'domesticContract',
											contract.domesticContract,
										)
									: renderContractFields(
											'overseasContract',
											contract.overseasContract,
										)}
							</div>
						</>
					) : (
						<div className={styles.detailContractInformationItemContainer}>
							{renderContractFields('termContract', contract.termContract)}
						</div>
					)}
				</div>
			</div>
			{/* 중도 해지 버튼 */}
			{/* {auth.user?.user_type === 'tourPro' && ( */}
			{/* <div className={styles.cancelButtonContainer}>
				<div className={styles.cancelButton} onClick={openModal}>
					중도해지
				</div>
			</div> */}
			{/* )} */}

			{/* 모달 컴포넌트 렌더링 */}
			<TerminationModal
				show={isModalOpen}
				onClose={closeModal}
				onConfirm={handleConfirmTermination}
				caddieName={counterpart?.name || '캐디'}
				tourList={sortedTourItems}
			/>
			{contract.termContract?.isOverseasIncluded === true ? (
				<TourSelectionModal
					show={isTourSelectionModalOpen}
					onClose={closeModal}
					isOverseasIncluded={true}
				/>
			) : (
				<TourSelectionModal
					show={isTourSelectionModalOpen}
					onClose={closeModal}
					isOverseasIncluded={false}
				/>
			)}
		</div>
	)
}

export default ContractManagementDetailView
